import { http } from "./http";

/**
 * Dynamic (admin-built) reports. The definition types mirror the C# model in
 * api/Aivrmanager.Api/Reports/ReportDefinition.cs; what the customer endpoint
 * returns has procedure names and parameter wiring stripped.
 */

export type ReportAudience = "customer" | "manager" | "admin";
export type ReportInputType = "text" | "number" | "date" | "datetime" | "bool" | "select";
export type ReportSectionKind = "table" | "stats" | "chart";
export type ReportParamSource = "input" | "customerId" | "managerId" | "timeZone" | "constant";
export type ReportColumnFormat =
  | "text" | "integer" | "decimal" | "currency" | "percent" | "bool" | "date" | "datetime" | "time"
  | "durationMmSs" | "durationHhMmSs" | "bytes";
export type ReportColumnTotal = "none" | "sum" | "avg" | "min" | "max" | "count";
export type ReportChartKind = "bar" | "line" | "pie";
export type ReportChartAggregate = "sum" | "count" | "avg" | "min" | "max";

export const COLUMN_FORMATS: { value: ReportColumnFormat; label: string }[] = [
  { value: "text", label: "Text" },
  { value: "integer", label: "Integer" },
  { value: "decimal", label: "Decimal" },
  { value: "currency", label: "Currency" },
  { value: "percent", label: "Percent" },
  { value: "bool", label: "Yes / No" },
  { value: "date", label: "Date" },
  { value: "datetime", label: "Date & time" },
  { value: "time", label: "Time" },
  { value: "durationMmSs", label: "Duration MMM:ss (seconds)" },
  { value: "durationHhMmSs", label: "Duration HH:mm:ss (seconds)" },
  { value: "bytes", label: "Bytes" },
];
export const COLUMN_TOTALS: ReportColumnTotal[] = ["none", "sum", "avg", "min", "max", "count"];

export interface ReportOption {
  value: string;
  label: string;
}

export interface ReportInput {
  key: string;
  label?: string;
  help?: string;
  type: ReportInputType;
  required?: boolean;
  /** Literal, or for dates: "today", "today-7", "today+1", "month" (1st of this month), "month+1", "now". */
  default?: string;
  options?: ReportOption[];
  /** Admin: procedure name; client: "*" when options come from the server. */
  optionsProcedure?: string;
  localToUtc?: boolean;
}

export interface ReportParam {
  name: string;
  sqlType?: string;
  source: ReportParamSource;
  inputKey?: string;
  constant?: string;
}

export interface ReportColumn {
  name: string;
  label?: string;
  sqlType?: string;
  visible?: boolean;
  format?: ReportColumnFormat;
  decimals?: number;
  align?: "left" | "right" | "center";
  total?: ReportColumnTotal;
  isLocalTime?: boolean;
}

export interface ReportChart {
  kind: ReportChartKind;
  categoryColumn?: string;
  seriesColumn?: string;
  valueColumn?: string;
  aggregate?: ReportChartAggregate;
  yColumns?: string[];
  stacked?: boolean;
  horizontal?: boolean;
  sort?: "none" | "value" | "category";
  topN?: number;
  valueFormat?: ReportColumnFormat;
  /** Value axis reaches at least this value. */
  yMax?: number;
  /** Numeric categories: always show every integer from `categoryFrom` up to at least `categoryTo`, missing values as 0. */
  categoryFrom?: number;
  categoryTo?: number;
}

export interface ReportSection {
  id: string;
  title?: string;
  kind: ReportSectionKind;
  /** Admin only. */
  procedure?: string;
  /** Admin only. */
  params?: ReportParam[];
  columns: ReportColumn[];
  filterColumns?: string[];
  chart?: ReportChart;
  maxRows?: number;
  pageSize?: number;
}

export interface ReportDefinition {
  version: number;
  autoRun?: boolean;
  inputs: ReportInput[];
  sections: ReportSection[];
}

export interface ResultColumn {
  name: string;
  sqlType?: string;
}

/** Rows come as arrays in column order; values are JSON primitives (datetimes as "yyyy-MM-ddTHH:mm:ss.fff"). */
export type ResultCell = string | number | boolean | null;

export interface SectionResult {
  sectionId: string;
  columns: ResultColumn[];
  rows: ResultCell[][];
  truncated: boolean;
  error?: string | null;
}

export interface ReportListItem {
  portalReportId: number;
  /** The permalink: /reports/custom/<name>. */
  name: string;
  menuLabel: string;
  description: string;
  /** Below 100: shown before the built-in reports in the menu; 100 and up: after them. */
  sortOrder: number;
}

export interface ReportDetail {
  portalReportId: number;
  name: string;
  menuLabel: string;
  description: string;
  definition: ReportDefinition;
  timeZone?: string | null;
}

const base = "/api/reports/custom";

export const portalReportsApi = {
  list: () => http<ReportListItem[]>(base),
  get: (name: string) => http<ReportDetail>(`${base}/${encodeURIComponent(name)}`),
  inputOptions: (name: string, key: string) => http<ReportOption[]>(`${base}/${encodeURIComponent(name)}/inputs/${encodeURIComponent(key)}/options`),
  run: (name: string, inputs: Record<string, string>) =>
    http<{ sections: SectionResult[] }>(`${base}/${encodeURIComponent(name)}/run`, { method: "POST", body: JSON.stringify({ inputs }) }),
};
